import React, { useState, useEffect, useCallback } from 'react';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';

const TITLE_OPTIONS = ['Dr.', 'Prof. Dr.', 'Assoc. Prof.', 'Consultant', 'Mr.', 'Ms.'];
const COMMON_SPECIALTIES = [
  'General Medicine',
  'Internal Medicine',
  'Rheumatology',
  'Cardiology',
  'Endocrinology',
  'Nephrology',
  'Neurology',
  'Gastroenterology',
  'Hematology',
  'Oncology',
  'Orthopedics',
  'Pediatrics',
  'Gynecology & Obstetrics',
  'Dermatology',
  'Pulmonology'
];

const INITIAL_CLINICIAN = {
  doctorId: 0,
  doctorCode: '',
  title: 'Dr.',
  name: '',
  degree: 'MBBS, MD',
  specialization: 'General Medicine',
  mobile: '',
  email: '',
  clinicName: '',
  address: '',
  centreId: null,
  isActive: true
};

export const ClinicianMasterPage = ({ showToast }) => {
  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_CLINICIAN);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // List State
  const [items, setItems] = useState([]);
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Confirmation Modal
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Load Centres
  useEffect(() => {
    masterService.getCentres({ isActive: true })
      .then(res => { if (res.success) setCentres(res.data || []); })
      .catch(() => {});
  }, []);

  // Fetch Clinicians for List
  const fetchClinicians = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getReferralDoctors({
        search: searchQuery.trim() || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        let list = res.data || [];
        if (specialtyFilter) {
          list = list.filter(d => d.specialization?.toLowerCase() === specialtyFilter.toLowerCase());
        }
        setItems(list);
      }
    } catch (err) {
      showToast?.('Failed to load referring clinicians.', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, specialtyFilter, statusFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchClinicians();
    }
  }, [viewMode, fetchClinicians]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Add New Form
  const handleAddNew = () => {
    const randomCode = `DOC${Math.floor(1000 + Math.random() * 9000)}`;
    setFormData({
      ...INITIAL_CLINICIAN,
      doctorCode: randomCode,
      centreId: centres[0]?.centreId || null
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Edit Row
  const handleEditRow = (row) => {
    setFormData({
      doctorId: row.doctorId,
      doctorCode: row.doctorCode || '',
      title: row.title || 'Dr.',
      name: row.name || '',
      degree: row.degree || '',
      specialization: row.specialization || 'General Medicine',
      mobile: row.mobile || '',
      email: row.email || '',
      clinicName: row.clinicName || '',
      address: row.address || '',
      centreId: row.centreId || null,
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Clinician Name is required.';
    if (!formData.doctorCode.trim()) errs.doctorCode = 'Clinician Code is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        doctorCode: formData.doctorCode.trim().toUpperCase(),
        title: formData.title,
        name: formData.name.trim(),
        degree: formData.degree.trim() || null,
        specialization: formData.specialization.trim() || null,
        mobile: formData.mobile.trim() || null,
        email: formData.email.trim() || null,
        clinicName: formData.clinicName.trim() || null,
        address: formData.address.trim() || null,
        centreId: formData.centreId ? parseInt(formData.centreId, 10) : null,
        isActive: formData.isActive
      };

      let res;
      if (isEditMode) {
        res = await masterService.updateReferralDoctor(formData.doctorId, payload);
      } else {
        res = await masterService.createReferralDoctor(payload);
      }

      if (res.success) {
        showToast?.(`Referring clinician ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchClinicians();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving referring clinician.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updateReferralDoctorStatus(confirmTarget.doctorId, newStatus);
      if (res.success) {
        showToast?.(`Clinician ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setItems(prev => prev.map(x => x.doctorId === confirmTarget.doctorId ? { ...x, isActive: newStatus } : x));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Centralized Standardized Pagination
  const {
    page,
    pageSize,
    totalRecords,
    pagedItems,
    setPage,
    setPageSize
  } = usePagination({
    items,
    resetDeps: [searchQuery, specialtyFilter, statusFilter]
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Referring Clinician' : 'Create Referring Clinician') : 'Referring Clinician Master'}
      masterName="Clinician"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            {/* Section 1: Identification */}
            <FormSection
              title="01. Professional Identification"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Doctor Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.doctorCode ? 'border-danger' : ''}`}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.doctorCode}
                    onChange={(e) => handleChange('doctorCode', e.target.value.toUpperCase())}
                    placeholder="e.g. DOC1001"
                    required
                  />
                  {errors.doctorCode && <span className="error-message">{errors.doctorCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Honorific Title</label>
                  <select
                    className="form-select"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                  >
                    {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Clinician Full Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.name ? 'border-danger' : ''}`}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g. Rajesh Sharma"
                    required
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Degrees & Qualifications</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.degree}
                    onChange={(e) => handleChange('degree', e.target.value)}
                    placeholder="e.g. MBBS, MD (Medicine), DM (Cardio)"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Medical Specialization</label>
                  <select
                    className="form-select"
                    value={formData.specialization}
                    onChange={(e) => handleChange('specialization', e.target.value)}
                  >
                    {COMMON_SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Primary Diagnostic Centre</label>
                  <select
                    className="form-select"
                    value={formData.centreId || ''}
                    onChange={(e) => handleChange('centreId', e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <option value="">-- All Processing Centres --</option>
                    {centres.map(c => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
                  </select>
                </div>
              </div>
            </FormSection>

            {/* Section 2: Contact & Clinic */}
            <FormSection
              title="02. Contact & Clinical Practice"
              iconColor="#10b981"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Mobile Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="doctor@clinic.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Hospital / Clinic Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.clinicName}
                    onChange={(e) => handleChange('clinicName', e.target.value)}
                    placeholder="e.g. City Heart & Diabetes Centre"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Clinic Address</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Chamber / OPD address"
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                    />
                    <span><strong>Clinician Active</strong> (Searchable in referring doctor lookup at registration)</span>
                  </label>
                </div>
              </div>
            </FormSection>

            {/* Form Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '140px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Clinician' : 'Save Clinician')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search doctor code, name, clinic..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '200px' }}
                value={specialtyFilter}
                onChange={(e) => { setSpecialtyFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Specializations</option>
                {COMMON_SPECIALTIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>

              <select
                className="form-select"
                style={{ width: '140px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ Add Clinician</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '120px' }}>Doctor Code</th>
                  <th>Clinician Name</th>
                  <th style={{ width: '180px' }}>Specialization</th>
                  <th style={{ width: '140px' }}>Mobile</th>
                  <th>Clinic / Hospital</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading referring clinicians catalog...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No clinicians matching your criteria.
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((r, index) => (
                    <tr key={r.doctorId || index}>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>
                        {r.doctorCode}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                            {r.title} {r.name}
                          </span>
                          {r.degree && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.degree}</span>}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--bg-tertiary)' }}>
                          {r.specialization || 'General'}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {r.mobile || '—'}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {r.clinicName || r.address || '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11.5px' }}
                            onClick={() => handleEditRow(r)}
                            title="Edit Clinician"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: r.isActive ? '#ef4444' : '#10b981'
                            }}
                            onClick={() => setConfirmTarget(r)}
                            title={r.isActive ? 'Deactivate Clinician' : 'Activate Clinician'}
                          >
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Clinician' : 'Activate Clinician'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.title} ${confirmTarget?.name}"?`
            : `Are you sure you want to activate "${confirmTarget?.title} ${confirmTarget?.name}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default ClinicianMasterPage;
